import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { partnerAppearance } from '@/components/ui/status'
import {
  fetchPartnerProfile,
  partnerProfileKey,
  refreshTokens,
  usePartnerProfile,
} from '@/features/auth/api'
import { useAuth } from '@/features/auth/auth-context'
import { startSession } from '@/features/auth/session'

const copyFor = (status: string) => {
  if (status === 'REJECTED') {
    return {
      title: 'auth.pending.rejectedTitle',
      body: 'auth.pending.rejectedDescription',
    } as const
  }

  if (status === 'SUSPENDED') {
    return {
      title: 'auth.pending.suspendedTitle',
      body: 'auth.pending.suspendedDescription',
    } as const
  }

  if (status === 'APPROVED') {
    return {
      title: 'auth.pending.approvedTitle',
      body: 'auth.pending.approvedDescription',
    } as const
  }

  return { title: 'auth.pending.title', body: 'auth.pending.description' } as const
}

export function PendingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { signOut } = useAuth()
  const profile = usePartnerProfile()
  const [checking, setChecking] = useState(false)
  const [unchanged, setUnchanged] = useState(false)

  const status = profile.data?.status ?? 'PENDING_APPROVAL'
  const copy = copyFor(status)

  useEffect(() => {
    if (status !== 'APPROVED') {
      return
    }

    const timer = setTimeout(() => {
      void navigate('/', { replace: true })
    }, 1200)

    return () => {
      clearTimeout(timer)
    }
  }, [status, navigate])

  const checkAgain = async () => {
    setChecking(true)
    setUnchanged(false)

    try {
      const tokens = await refreshTokens()
      startSession(tokens)
    } catch {
      setUnchanged(true)
    }

    const updated = await queryClient
      .fetchQuery({
        queryKey: partnerProfileKey,
        queryFn: ({ signal }) => fetchPartnerProfile(signal),
        staleTime: 0,
      })
      .catch(() => null)

    setChecking(false)

    if (updated === null || updated.status !== 'APPROVED') {
      setUnchanged(true)
    }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <div className="flex flex-col gap-3 panel rounded-lg p-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-ink">{t(copy.title)}</h1>
          <Badge {...partnerAppearance[status]}>{t(`status.partner.${status}`)}</Badge>
        </div>

        <p className="text-sm text-ink-muted">{t(copy.body)}</p>

        {status === 'REJECTED' && profile.data?.rejectionReason !== undefined ? (
          <p className="rounded-lg border border-error/40 bg-error-surface p-3 text-sm text-ink">
            <span className="font-semibold">{`${t('auth.pending.reason')}: `}</span>
            {profile.data.rejectionReason}
          </p>
        ) : null}

        {status === 'PENDING_APPROVAL' ? (
          <>
            <p className="text-sm text-ink-muted">{t('auth.pending.meanwhile')}</p>

            <div className="flex flex-wrap gap-2">
              <Button icon="refresh" loading={checking} onClick={() => void checkAgain()}>
                {checking ? t('auth.pending.checking') : t('auth.pending.checkAgain')}
              </Button>
              <Button variant="secondary" icon="code" onClick={() => void navigate('/sdk')}>
                {t('auth.pending.goToSdk')}
              </Button>
              <Button variant="secondary" icon="help" onClick={() => void navigate('/faq')}>
                {t('auth.pending.goToFaq')}
              </Button>
            </div>

            {unchanged ? (
              <p role="status" className="flex items-center gap-2 text-sm text-ink-muted">
                <Icon name="clock" className="size-4" />
                {t('auth.pending.stillPending')}
              </p>
            ) : null}
          </>
        ) : null}

        <div>
          <Button variant="ghost" icon="logOut" onClick={() => void signOut()}>
            {t('auth.pending.signOut')}
          </Button>
        </div>
      </div>
    </div>
  )
}
